import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../common/Modal';
import Table from '../common/table/Table';
import Pagination from '../common/table/Pagination';
import { fetchUserMemories, deleteUserMemory } from '../../api/settings';
import PillButton from '../common/button/PillButton';
import ConfirmationDialog from '../common/ConfirmationDialog';

const formatDateTime = date => {
    try {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).format(date);
    } catch (e) {
        const d = new Date(date);
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    }
};

const PersonalMemoriesModal = ({ isOpen, onClose, agentId = null }) => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmState, setConfirmState] = useState({ open: false, id: null });

    const load = useCallback(async (nextPage = 1, nextLimit = limit) => {
        try {
            setLoading(true);
            setError(null);
            const offset = (nextPage - 1) * nextLimit;
            const res = await fetchUserMemories({ limit: nextLimit, offset, agentId });
            setRows(res?.data || []);
            setTotal(res?.total || 0);
            setPage(nextPage);
            setLimit(nextLimit);
        } catch (e) {
            setError('Failed to load memories');
        } finally {
            setLoading(false);
        }
    }, [limit, agentId]);

    useEffect(() => {
        if (isOpen) load(1, limit);
    }, [isOpen, load, limit, agentId]);

    const handleDelete = useCallback(async id => {
        try {
            await deleteUserMemory(id, { agentId });
            // Reload current page; if last item removed and became empty, move page back
            const isLastItemOnPage = rows.length === 1 && page > 1;
            const nextPage = isLastItemOnPage ? page - 1 : page;
            await load(nextPage, limit);
        } catch (e) {
            setError('Failed to delete memory');
        }
    }, [rows, page, limit, load, agentId]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Personal Memories" isLarge>
            {error && <div style={{ color: 'var(--color-error-text)', textAlign: 'center' }}>{error}</div>}
            {loading && <div style={{ textAlign: 'center' }}>Loading...</div>}
            {!loading && (
                <>
                    <Table
                        columns={[
                            { key: 'date', title: 'Date', dataIndex: 'createdAt', render: v => formatDateTime(new Date(v)), style: { minWidth: rows.length > 0 ? '8rem' : '0' } },
                            { key: 'text', title: 'Text', dataIndex: 'text', style: { minWidth: rows.length > 0 ? '8rem' : '0' } },
                            { key: 'action', title: '', dataIndex: 'id', align: 'right', render: id => (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <PillButton
                                        size="small"
                                        variant="secondary"
                                        onClick={() => setConfirmState({ open: true, id })}
                                        title="Delete memory"
                                    >
                                        Delete
                                    </PillButton>
                                </div>
                            ) },
                        ]}
                        rows={rows}
                        keyField="id"
                    />
                    {rows.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Pagination
                                page={page}
                                limit={limit}
                                total={total}
                                onChange={nextPage => load(nextPage, limit)}
                            />
                        </div>
                    )}
                </>
            )}

            <ConfirmationDialog
                isOpen={confirmState.open}
                onClose={() => setConfirmState({ open: false, id: null })}
                onConfirm={() => {
                    const id = confirmState.id;
                    setConfirmState({ open: false, id: null });
                    if (id) handleDelete(id);
                }}
                title="Delete memory?"
                message="This action is irreversible. Do you really want to delete this memory?"
                confirmText="Delete"
                cancelText="Cancel"
                dangerous
            />
        </Modal>
    );
};

export default PersonalMemoriesModal;
